import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import DOMPurify from 'dompurify';
import {
    ActionIcon,
    Box,
    Button,
    Card,
    Flex,
    Group,
    Loader,
    ScrollArea,
    Stack,
    Switch,
    Text,
    Textarea,
    Title,
    Tooltip,
} from '@mantine/core';
import { IconArrowLeft, IconDeviceFloppy, IconEdit, IconRefresh } from '@tabler/icons-react';
import { toast } from 'react-toastify';

import ResizablePanels from '../components/ResizablePanels';
import DocumentPreviewPane from '../components/DocumentPreviewPane';
import FormulaRenderer from '../components/FormulaRenderer';
import {
    getUploadedFilePageChunks,
    updateUploadedFilePageChunks,
} from '../api/uploadedFileApi';
import { useAuth } from '../customAuth/authStateHook';
import { UploadedFilePageChunksResponse } from '../models/responses/UploadedFilePageChunkResponse';

type EditablePage = {
    pageNumber: string;
    content: string;
};

type ReadonlySegment =
    | { type: 'text'; value: string }
    | { type: 'table'; value: string };

const normalizePageChunks = (pageChunks: Record<string, string>): EditablePage[] =>
    Object.entries(pageChunks ?? {})
        .map(([pageNumber, content]) => ({
            pageNumber,
            content: content ?? '',
        }))
        .sort((left, right) => Number(left.pageNumber) - Number(right.pageNumber));

const htmlTableRegex = /<table[\s\S]*?<\/table>/gi;

const splitReadonlySegments = (content: string): ReadonlySegment[] => {
    const segments: ReadonlySegment[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = htmlTableRegex.exec(content)) !== null) {
        const textBefore = content.slice(lastIndex, match.index);
        if (textBefore.trim()) {
            segments.push({ type: 'text', value: textBefore });
        }

        segments.push({ type: 'table', value: match[0] });
        lastIndex = match.index + match[0].length;
    }

    const trailingText = content.slice(lastIndex);
    if (trailingText.trim() || segments.length === 0) {
        segments.push({ type: 'text', value: trailingText });
    }

    return segments;
};

const sanitizeTableMarkup = (markup: string): string =>
    DOMPurify.sanitize(markup, {
        ALLOWED_TAGS: ['table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'caption', 'colgroup', 'col', 'br', 'strong', 'b', 'em', 'i', 'p', 'span', 'sub', 'sup'],
        ALLOWED_ATTR: ['rowspan', 'colspan', 'scope', 'style'],
    });

const FormattedReadonlyContent = ({ content }: { content: string }) => {
    const segments = useMemo(() => splitReadonlySegments(content), [content]);

    if (!content.trim()) {
        return <>No content</>;
    }

    return (
        <Box className="formatted-readonly-content">
            {segments.map((segment, index) => {
                if (segment.type === 'table') {
                    return (
                        <Box
                            key={`table-${index}`}
                            className="formatted-readonly-table"
                            dangerouslySetInnerHTML={{ __html: sanitizeTableMarkup(segment.value) }}
                        />
                    );
                }

                return (
                    <Box key={`text-${index}`} style={{ marginBottom: index < segments.length - 1 ? '8px' : 0 }}>
                        <FormulaRenderer text={segment.value} />
                    </Box>
                );
            })}
        </Box>
    );
};

export default function DocumentContentEditorView() {
    const navigate = useNavigate();
    const { uploadedFileId } = useParams<{ uploadedFileId: string }>();
    const { canAccessInternalTools } = useAuth();
    const [documentContent, setDocumentContent] = useState<UploadedFilePageChunksResponse | null>(null);
    const [pages, setPages] = useState<EditablePage[]>([]);
    const [editingPages, setEditingPages] = useState<Record<string, boolean>>({});
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isReingesting, setIsReingesting] = useState(false);
    const [useFormattedReadonlyView, setUseFormattedReadonlyView] = useState(false);

    const loadContent = async () => {
        if (!uploadedFileId) {
            navigate('/folder-explorer', { replace: true });
            return;
        }

        setIsLoading(true);

        try {
            const result = await getUploadedFilePageChunks(Number(uploadedFileId));
            setDocumentContent(result);
            setPages(normalizePageChunks(result.page_chunks));
        } catch (error) {
            console.error('Failed to load uploaded file page chunks', error);

            if (axios.isAxiosError(error) && error.response?.status === 404) {
                toast.info('No extracted page content was found for this document.');
            } else {
                toast.error('Failed to load document content.');
            }

            navigate('/folder-explorer', { replace: true });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!canAccessInternalTools) {
            navigate('/access-denied', { replace: true });
            return;
        }

        loadContent();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uploadedFileId, canAccessInternalTools]);

    const pageChunksForSave = useMemo<Record<string, string>>(
        () => Object.fromEntries(pages.map((page) => [page.pageNumber, page.content])),
        [pages],
    );

    const previewFile = useMemo(
        () => ({
            FileName: documentContent?.file_name ?? '',
            Url: documentContent?.file_url ?? '',
        }),
        [documentContent?.file_name, documentContent?.file_url],
    );

    const updatePageContent = (pageNumber: string, content: string) => {
        setPages((current) =>
            current.map((page) => (page.pageNumber === pageNumber ? { ...page, content } : page)),
        );
    };

    const toggleEdit = (pageNumber: string) => {
        setEditingPages((current) => ({
            ...current,
            [pageNumber]: !current[pageNumber],
        }));
    };

    const handleSave = async (reingest = false) => {
        if (!uploadedFileId) {
            return;
        }

        try {
            if (reingest) {
                setIsReingesting(true);
            } else {
                setIsSaving(true);
            }

            const response = await updateUploadedFilePageChunks(
                Number(uploadedFileId),
                pageChunksForSave,
                { reingest },
            );
            toast.success(response.message || 'Document content updated successfully.');
            setEditingPages({});
        } catch (error) {
            console.error('Failed to update uploaded file page chunks', error);
            toast.error(reingest ? 'Failed to update and reingest document content.' : 'Failed to update document content.');
        } finally {
            if (reingest) {
                setIsReingesting(false);
            } else {
                setIsSaving(false);
            }
        }
    };

    const preview = (
        <Card
            shadow="sm"
            radius="lg"
            p={"lg"}
            style={{
                height: '100%',
                minHeight: 0,
                border: '1px solid #e0d8cc',
                background: 'linear-gradient(180deg, #fffdf9 0%, #f8f3eb 100%)',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
            }}
        >
            <Flex
                align="center"
                justify="space-between"
                px="lg"
                py="lg"
                style={{
                    border: '1px solid #e7dfd3',
                    borderRadius: '12px',
                    background: '#fffdf9',
                }}
            >
                <Box>
                    <Text size="xs" fw={700} tt="uppercase" style={{ letterSpacing: '0.08em', color: '#8a6a4a' }}>
                        Document Preview
                    </Text>
                    <Text size="sm" c="dimmed">
                        Compare each page entry with the original file
                    </Text>
                </Box>
                <Tooltip label="Reload preview" withArrow>
                    <ActionIcon
                        variant="subtle"
                        radius="md"
                        onClick={loadContent}
                        style={{ color: '#8a6a4a', border: '1px solid rgba(4, 79, 40, 0.12)', background: '#ffffff', marginRight: '8px' }}
                    >
                        <IconRefresh size={16} />
                    </ActionIcon>
                </Tooltip>
            </Flex>

            <Box
                style={{
                    flex: 1,
                    minHeight: 0,
                    padding: '16px',
                    border: '1px solid #ebe4d9',
                    borderRadius: '12px',
                    background: '#fffdf9',
                }}
            >
                {isLoading ? (
                    <Flex align="center" justify="center" style={{ height: '100%' }}>
                        <Loader color="#8a6a4a" />
                    </Flex>
                ) : (
                    <DocumentPreviewPane file={previewFile} />
                )}
            </Box>
        </Card>
    );

    const content = (
        <Card
            shadow="sm"
            radius="lg"
            p="lg"
            style={{
                height: '100%',
                minHeight: 0,
                border: '1px solid #e0d8cc',
                background: 'linear-gradient(180deg, #fffdf9 0%, #f8f3eb 100%)',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
            }}
        >
            <Flex
                align="flex-start"
                justify="space-between"
                px="lg"
                py="lg"
                gap="md"
                wrap="wrap"
                style={{
                    border: '1px solid #e7dfd3',
                    borderRadius: '12px',
                    background: '#fffdf9',
                }}
            >
                <Box style={{ flex: '1 1 280px', minWidth: 0 }}>
                    <Text size="xs" fw={700} tt="uppercase" style={{ letterSpacing: '0.08em', color: '#8a6a4a' }}>
                        Extracted Pages
                    </Text>
                    <Text size="sm" c="dimmed">
                        Toggle formatted readonly rendering for tables, formulas, and markdown headers
                    </Text>
                </Box>
                <Box
                    style={{
                        marginLeft: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-end',
                        gap: '10px',
                        flex: '0 1 auto',
                        marginTop: '8px',
                        marginRight: '8px',
                    }}
                >
                    <Switch
                        checked={useFormattedReadonlyView}
                        onChange={(event) => setUseFormattedReadonlyView(event.currentTarget.checked)}
                        color="#8a6a4a"
                        label="Formatted readonly view"
                        styles={{
                            trackLabel: { fontWeight: 600 },
                            label: { color: '#8a6a4a', fontWeight: 600 },
                        }}
                    />
                    <Group gap="xs" justify="flex-end" wrap="wrap">
                        <Button
                            size="xs"
                            variant="light"
                            color="#8a6a4a"
                            leftSection={isReingesting ? <Loader size={12} color="#8a6a4a" /> : <IconRefresh size={14} />}
                            onClick={() => handleSave(true)}
                            loading={isReingesting}
                            disabled={isSaving}
                            style={{ minWidth: '132px', height: '30px', justifyContent: 'center', paddingInline: '10px' }}
                        >
                            Save and Reingest
                        </Button>
                        <Button
                            size="xs"
                            leftSection={isSaving ? <Loader size={12} color="#fff" /> : <IconDeviceFloppy size={14} />}
                            onClick={() => handleSave(false)}
                            loading={isSaving}
                            disabled={isReingesting}
                            color="#8a6a4a"
                            style={{ background: '#8a6a4a', minWidth: '128px', height: '30px', justifyContent: 'center', paddingInline: '10px' }}
                        >
                            Save Changes
                        </Button>
                    </Group>
                </Box>
            </Flex>

            <ScrollArea
                style={{
                    flex: 1,
                    minHeight: 0,
                    border: '1px solid #ebe4d9',
                    borderRadius: '12px',
                    background: '#fffdf9',
                }}
            >
                <Stack gap="lg" p="lg">
                    {isLoading ? (
                        <Flex align="center" justify="center" py="xl">
                            <Loader color="#8a6a4a" />
                        </Flex>
                    ) : pages.length === 0 ? (
                        <Card radius="md" p="lg" style={{ border: '1px dashed rgba(4, 79, 40, 0.22)', background: '#ffffff' }}>
                            <Text ta="center" c="dimmed">No page content available for this document.</Text>
                        </Card>
                    ) : (
                        pages.map((page) => {
                            const isEditing = !!editingPages[page.pageNumber];

                            return (
                                <Card
                                    key={page.pageNumber}
                                    radius="lg"
                                    padding="lg"
                                    style={{
                                        border: '1px solid rgba(4, 79, 40, 0.16)',
                                        background: '#ffffff',
                                        boxShadow: '0 8px 24px rgba(4, 79, 40, 0.06)',
                                    }}
                                >
                                    <Flex align="center" justify="space-between" gap="md" wrap="wrap" mb="sm">
                                        <Box>
                                            <Text fw={700} c="#8a6a4a">Page {page.pageNumber}</Text>
                                            <Text size="sm" c="dimmed">
                                                {page.content.trim() ? `${page.content.trim().length.toLocaleString()} characters` : 'No content'}
                                            </Text>
                                        </Box>

                                        <Button
                                            size="xs"
                                            variant={isEditing ? 'filled' : 'light'}
                                            color="#8a6a4a"
                                            leftSection={<IconEdit size={14} />}
                                            onClick={() => toggleEdit(page.pageNumber)}
                                            style={{ height: '28px', paddingInline: '10px' }}
                                        >
                                            {isEditing ? 'Done Editing' : 'Edit Page'}
                                        </Button>
                                    </Flex>

                                    {isEditing ? (
                                        <Textarea
                                            value={page.content}
                                            autosize
                                            minRows={10}
                                            maxRows={24}
                                            onChange={(event) => updatePageContent(page.pageNumber, event.currentTarget.value)}
                                            styles={{
                                                input: {
                                                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                                                    fontSize: '13px',
                                                    lineHeight: 1.6,
                                                    borderRadius: '14px',
                                                    border: '1px solid rgba(4, 79, 40, 0.16)',
                                                    background: '#F8FAF8',
                                                },
                                            }}
                                        />
                                    ) : (
                                        <Box
                                            style={{
                                                fontSize: '13.5px',
                                                lineHeight: 1.7,
                                                color: '#1f2937',
                                                padding: '14px 16px',
                                                borderRadius: '14px',
                                                border: '1px solid rgba(4, 79, 40, 0.12)',
                                                background: '#F8FAF8',
                                            }}
                                        >
                                            {useFormattedReadonlyView ? (
                                                <FormattedReadonlyContent content={page.content} />
                                            ) : (
                                                <Box style={{ whiteSpace: 'pre-wrap' }}>
                                                    {page.content || 'No content'}
                                                </Box>
                                            )}
                                        </Box>
                                    )}
                                </Card>
                            );
                        })
                    )}
                </Stack>
            </ScrollArea>
        </Card>
    );

    return (
        <Box p="xl" style={{ minHeight: '100vh', background: '#faf8f4' }}>
            <Flex align="center" justify="space-between" gap="md" wrap="wrap" mb="lg">
                <Box>
                    <Title order={2} style={{ color: '#8a6a4a', letterSpacing: '-0.02em' }}>
                        Document Content
                    </Title>
                    <Text size="sm" c="dimmed">
                        {documentContent
                            ? `${documentContent.file_name} | ${documentContent.page_count} pages`
                            : 'Loading document details...'}
                    </Text>
                </Box>
                <Button
                    variant="filled"
                    size="xs"
                    color="dark"
                    leftSection={<IconArrowLeft size={14} />}
                    onClick={() => navigate('/folder-explorer')}
                    style={{
                        background: '#8a6a4a',
                        color: '#ffffff',
                        paddingInline: '14px',
                        height: '34px',
                        boxShadow: '0 10px 22px rgba(4, 79, 40, 0.16)',
                    }}
                >
                    Back to Documents
                </Button>
            </Flex>

            <Box style={{ height: 'calc(100vh - 170px)', minHeight: 0, padding: '6px 0 8px', background: '#faf8f4' }}>
                <ResizablePanels left={preview} right={content} />
            </Box>
        </Box>
    );
}

